import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ArrowUpRight, CalendarClock, Code2 } from 'lucide-react';
import { createSoftwareSlug } from '../utils/urlHelpers';
import {
  buildSoftwareListSeoParagraph,
  formatCategoryLastUpdate,
  getSoftwareMemberGrowth,
  latestForumUpdateIso,
} from '../utils/softwareAggregates';
import { aggregateCategoryForums } from '../utils/categoryAggregates';
import type { ForumBase } from '../types/forum';

interface SoftwareStatRow {
  software: string;
  forum_count: number;
}

interface Props {
  stats: SoftwareStatRow[];
  forums: ForumBase[];
  darkMode: boolean;
}

const SoftwarePageHero: React.FC<Props> = ({ stats, forums, darkMode }) => {
  const agg = useMemo(() => aggregateCategoryForums(forums), [forums]);
  const lastUpdateIso = useMemo(() => latestForumUpdateIso(forums), [forums]);

  const growing = useMemo(() => {
    return stats
      .map((s) => {
        const swForums = forums.filter((f) => f.software === s.software);
        const pct = getSoftwareMemberGrowth(swForums);
        if (pct === null) return null;
        return { software: s.software, growthPercent: pct, forumCount: s.forum_count };
      })
      .filter((x): x is { software: string; growthPercent: number; forumCount: number } => x !== null)
      .sort((a, b) => b.growthPercent - a.growthPercent)
      .slice(0, 5);
  }, [stats, forums]);

  const seoParagraph = useMemo(
    () => buildSoftwareListSeoParagraph(stats.length, agg, lastUpdateIso),
    [stats.length, agg, lastUpdateIso]
  );

  const card = darkMode
    ? 'bg-gray-800/80 border border-gray-700'
    : 'bg-white shadow-sm border border-violet-100';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10 items-stretch">
      <section className={`rounded-xl p-4 sm:p-6 h-full ${card}`} aria-labelledby="growing-software-heading">
        <h2
          id="growing-software-heading"
          className={`text-lg font-semibold flex items-center gap-2 mb-4 ${
            darkMode ? 'text-white' : 'text-violet-900'
          }`}
        >
          <TrendingUp className="text-green-500" size={22} />
          En çok büyüyen yazılımlar
        </h2>
        {growing.length === 0 ? (
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Karşılaştırma verisi olan yazılımlarda üye artışı henüz görünmüyor.
          </p>
        ) : (
          <ul className="space-y-3">
            {growing.map((s, i) => (
              <li key={s.software}>
                <Link
                  to={`/software/${createSoftwareSlug(s.software)}`}
                  className={`flex items-center justify-between gap-3 p-3 rounded-lg transition-colors ${
                    darkMode ? 'hover:bg-gray-700' : 'hover:bg-violet-50'
                  }`}
                >
                  <span className="flex items-center gap-3 min-w-0">
                    <span
                      className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                        darkMode ? 'bg-violet-900 text-violet-300' : 'bg-violet-100 text-violet-700'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="flex items-center gap-2 min-w-0">
                      <Code2 size={16} className="text-violet-500 shrink-0" />
                      <span className={`font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {s.software}
                      </span>
                    </span>
                  </span>
                  <span className="flex flex-col items-end shrink-0 text-green-500 text-sm font-medium">
                    <span className="flex items-center gap-1">
                      <ArrowUpRight size={14} />
                      +{s.growthPercent.toFixed(1)}% üye
                    </span>
                    <span className={`text-xs font-normal ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {s.forumCount} forum
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={`rounded-xl p-4 sm:p-6 h-full ${card}`} aria-labelledby="software-list-overview-heading">
        <h2
          id="software-list-overview-heading"
          className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-violet-900'}`}
        >
          Yazılımlar özeti
        </h2>
        <p className={`text-sm mb-4 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <CalendarClock size={16} className="shrink-0" />
          Son güncelleme: {formatCategoryLastUpdate(lastUpdateIso)}
        </p>
        <dl className={`grid grid-cols-2 gap-3 text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <div>
            <dt className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Yazılım</dt>
            <dd className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {stats.length}
            </dd>
          </div>
          <div>
            <dt className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Aktif forum</dt>
            <dd className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {agg.forumCount.toLocaleString('tr-TR')}
            </dd>
          </div>
          <div>
            <dt className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Toplam üye</dt>
            <dd className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {agg.totalMembers.toLocaleString('tr-TR')}
            </dd>
          </div>
          <div>
            <dt className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Toplam ileti</dt>
            <dd className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {agg.totalPosts.toLocaleString('tr-TR')}
            </dd>
          </div>
        </dl>
        <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {seoParagraph}
        </p>
      </section>
    </div>
  );
};

export default SoftwarePageHero;
